import { format, endOfDay, startOfDay } from 'date-fns'
import { TZDate } from '@date-fns/tz'

declare global {
  interface Window {
    pktTz: string
  }
}

window.pktTz = window.pktTz === undefined ? 'Europe/Oslo' : window.pktTz

/**
 * Returns the current date in the specified timezone, with time set to 00:00:00
 * @param tz - Timezone string (default: window.pktTz)
 * @returns Date object representing today's date in the specified timezone
 */
export const todayInTz = (tz: string = window.pktTz): Date => {
  const now = new TZDate(new Date().toISOString(), tz)
  now.setHours(0, 0, 0, 0)

  if (isNaN(now.getTime())) {
    return new Date()
  }
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

/**
 * Parses an ISO date string and returns a Date object.
 * Handles "YYYY-MM-DD", "YYYY-MM", and "YYYY" formats as local dates.
 * @param date - The ISO date string to parse
 * @returns A Date object
 */
export function parseISODateString(date: string | null | undefined): Date {
  if (!date || date === '') return new Date()
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    // "YYYY-MM-DD" — always parse as local time!
    const [year, month, day] = date.split('-').map(Number)
    return new Date(year, month - 1, day)
  }
  if (/^\d{4}-\d{2}$/.test(date)) {
    // "YYYY-MM"
    const [year, month] = date.split('-').map(Number)
    return new Date(year, month - 1, 1)
  }
  if (/^\d{4}$/.test(date)) {
    // "YYYY"
    return new Date(Number(date), 0, 1)
  }
  // Fallback
  return new Date(date)
}

/**
 * Formats a Date or TZDate object to an ISO date string (YYYY-MM-DD).
 * Uses local time, not UTC.
 * @param date - The Date or TZDate object to format
 * @returns An ISO date string
 */
export const formatISODate = (date: Date | TZDate): string => {
  if (!date || isNaN(date.getTime())) return ''
  // Use local time, not UTC!
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Converts an ISO date string to a Date object
 * @param date - The ISO date string to convert
 * @returns A Date object or null if invalid
 */
export const fromISOToDate = (date: string | null): Date | null => {
  if (!date) {
    return null
  }
  // Always parse to local Date first!
  const localDate = parseISODateString(date)
  if (isNaN(localDate.getTime())) {
    return null
  }
  return localDate
}

/**
 * Converts an ISO date string to a formatted date string
 * @param date - The ISO date string to convert
 * @param dateformat - The desired date format
 * @returns A formatted date string
 */
export const fromISOtoLocal = (date: string, dateformat: string): string => {
  const dateObj = newDate(date)
  if (isNaN(dateObj.getTime())) {
    return ''
  }
  return format(dateObj, dateformat)
}

/**
 * Creates a new Date object based on the provided date string or Date object.
 * Optionally sets the time to the start or end of the day.
 * @param date - The date string or Date object
 * @param timeOfDay - 'start' to set time to 00:00:00, 'end' to set time to 23:59:59
 * @returns A Date object
 */
export const newDate = (date: string | Date = '', timeOfDay?: 'start' | 'end'): Date => {
  const dateObject = typeof date === 'string' ? parseISODateString(date) : date
  if (!dateObject || isNaN(dateObject.getTime())) {
    return new Date()
  }
  if (timeOfDay) {
    return timeOfDay === 'end' ? endOfDay(dateObject) : startOfDay(dateObject)
  }
  return dateObject
}

/**
 * Creates a new Date object based on the provided year, month, and day.
 * If the inputs are invalid, returns the current date.
 * @param year - The year
 * @param month - The month (0-11)
 * @param day - The day of the month (default: 1)
 * @returns A Date object
 */
export const newDateYMD = (year: number, month: number, day: number = 1): Date => {
  if (
    typeof year !== 'number' ||
    typeof month !== 'number' ||
    typeof day !== 'number' ||
    isNaN(year) ||
    isNaN(month) ||
    isNaN(day)
  ) {
    return new Date()
  }
  const date = new Date(year, month, day)
  if (isNaN(date.getTime())) {
    return new Date()
  }
  return date
}

/**
 * Creates a new Date object based on the provided date string or Date object.
 * Sets the time to noon (12:00:00) to avoid timezone issues.
 * @param date - The date string or Date object
 * @returns A Date object with time set to noon
 */
export const newDateFromDate = (date: Date | TZDate | number): Date => {
  let tzDate: TZDate
  if (typeof date === 'number') {
    tzDate = new TZDate(date as number, window.pktTz)
  } else {
    tzDate = new TZDate(date, window.pktTz)
  }

  tzDate.setHours(12, 0, 0, 0)
  return tzDate
}

/**
 * Formats a Date or TZDate object to a human-readable date string in Norwegian.
 * @param date - The Date or TZDate object to format
 * @returns A human-readable date string
 */
let _readableDateFormatter: Intl.DateTimeFormat | null = null
let _readableDateTz: string | undefined = undefined

export const formatReadableDate = (date: Date | TZDate): string => {
  if (!date || isNaN(date.getTime())) return ''
  const tz = typeof window !== 'undefined' ? window.pktTz : undefined
  if (!_readableDateFormatter || _readableDateTz !== tz) {
    _readableDateFormatter = new Intl.DateTimeFormat('no', { dateStyle: 'full', timeZone: tz })
    _readableDateTz = tz
  }
  return _readableDateFormatter.format(date)
}

/**
 * Validates if a date is within the specified range
 * @param date - The date to validate
 * @param min - The minimum date (ISO string)
 * @param max - The maximum date (ISO string)
 * @returns boolean indicating if the date is in range
 */
export const isDateInRange = (date: Date, min?: string | null, max?: string | null): boolean => {
  if (!date || isNaN(date.getTime())) return false

  const minDate = min ? newDate(min) : null
  const maxDate = max ? newDate(max) : null

  if (minDate && date < minDate) return false
  if (maxDate && date > maxDate) return false

  return true
}

/**
 * Checks if a date is in the list of excluded dates
 * @param date - The date to check
 * @param excludedDates - Array of excluded date strings (ISO format)
 * @returns boolean indicating if the date is excluded
 */
export const isDateExcluded = (date: Date, excludedDates: string[]): boolean => {
  if (!date || isNaN(date.getTime()) || !Array.isArray(excludedDates)) return false

  const dateISO = formatISODate(date)
  return excludedDates.includes(dateISO)
}

/**
 * Checks if a date's weekday is excluded
 * @param date - The date to check
 * @param excludedWeekdays - Array of excluded weekdays (0 = Sunday, 1 = Monday, etc.)
 * @returns boolean indicating if the weekday is excluded
 */
export const isWeekdayExcluded = (date: Date, excludedWeekdays: string[]): boolean => {
  if (!date || isNaN(date.getTime()) || !Array.isArray(excludedWeekdays)) return false

  const dayOfWeek = date.getDay().toString() // 0 = Sunday, 1 = Monday, etc.
  return excludedWeekdays.includes(dayOfWeek)
}

/**
 * Validates if a date can be selected based on all constraints
 * @param date - The date to validate
 * @param min - The minimum date (ISO string)
 * @param max - The maximum date (ISO string)
 * @param excludedDates - Array of excluded date strings (ISO format)
 * @param excludedWeekdays - Array of excluded weekdays (0 = Sunday, 1 = Monday, etc.)
 * @returns boolean indicating if the date is selectable
 */
export const isDateSelectable = (
  date: Date,
  min?: string | null,
  max?: string | null,
  excludedDates?: string[],
  excludedWeekdays?: string[],
): boolean => {
  if (!date || isNaN(date.getTime())) return false

  if (!isDateInRange(date, min, max)) return false
  if (excludedDates && isDateExcluded(date, excludedDates)) return false
  if (excludedWeekdays && isWeekdayExcluded(date, excludedWeekdays)) return false

  return true
}

/**
 * Filters an array of date strings to only include selectable dates
 * @param dates - Array of date strings (ISO format)
 * @param min - The minimum date (ISO string)
 * @param max - The maximum date (ISO string)
 * @param excludedDates - Array of excluded date strings (ISO format)
 * @param excludedWeekdays - Array of excluded weekdays (0 = Sunday, 1 = Monday, etc.)
 * @returns Array of selectable date strings
 */
export const filterSelectableDates = (
  dates: string[],
  min?: string | null,
  max?: string | null,
  excludedDates?: string[],
  excludedWeekdays?: string[],
): string[] => {
  return dates.filter((dateStr) => {
    const date = fromISOToDate(dateStr)
    return date && isDateSelectable(date, min, max, excludedDates, excludedWeekdays)
  })
}

/**
 * Sorts an array of ISO date strings chronologically
 * @param dates - Array of date strings (ISO format)
 * @returns Sorted array of date strings
 */
export const sortDateStrings = (dates: string[]): string[] => {
  return dates.slice().sort((a, b) => {
    const dateA = fromISOToDate(a)
    const dateB = fromISOToDate(b)
    if (!dateA || !dateB) return 0
    return dateA.getTime() - dateB.getTime()
  })
}

/**
 * Validates a date range (start should be before or equal to end)
 * @param startDate - The start date (ISO string)
 * @param endDate - The end date (ISO string)
 * @returns boolean indicating if the date range is valid
 */
export const isValidDateRange = (startDate: string, endDate: string): boolean => {
  const start = fromISOToDate(startDate)
  const end = fromISOToDate(endDate)

  if (!start || !end) return false

  return start <= end
}

export default {
  todayInTz,
  parseISODateString,
  formatISODate,
  fromISOToDate,
  fromISOtoLocal,
  newDate,
  newDateYMD,
  newDateFromDate,
  formatReadableDate,
  isDateInRange,
  isDateExcluded,
  isWeekdayExcluded,
  isDateSelectable,
  filterSelectableDates,
  sortDateStrings,
  isValidDateRange,
}
